import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { ExpandIcon } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type ProjectSettingsAccordionProps = {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
};

export const ProjectSettingsAccordion = ({
  title,
  description,
  children,
  defaultExpanded = false,
}: ProjectSettingsAccordionProps): ReactElement => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      onChange={(event, nextExpanded) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
          return;
        }
        setExpanded(nextExpanded);
      }}
      sx={[
        settingsPanelSurfaceSx,
        {
          border: 'none',
          borderRadius: r('card'),
          overflow: 'hidden',
          padding: s('lg'),
          '&::before': { display: 'none' },
        },
      ]}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon fontSize="medium" sx={{ fontSize: 28 }} />}
        sx={{
          padding: s('none'),
          minHeight: 'auto',
          alignItems: 'flex-start',
          cursor: 'default !important',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          '&.MuiButtonBase-root': {
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content': {
            margin: s('none'),
            alignItems: 'flex-start',
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content .MuiTypography-root': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            alignSelf: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
          },
        }}
      >
        <Stack gap="xs" sx={{ width: '100%' }}>
          <Text role="sectionTitle">{title}</Text>
          {description ? (
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {description}
            </Text>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: s('none'), paddingTop: s('lg') }}>
        <Panel container="none" padding="xs" border={false}>
          {children}
        </Panel>
      </AccordionDetails>
    </Accordion>
  );
};
